import React, { useMemo } from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getAssets, saveAssetsBatch } from '../../api/assets';
import { useAssetTypes } from '../../hooks/useAssetTypes';
import { useCrcies } from '../../hooks/useCrcies';
import { useBenchmarkRates } from '../../hooks/useBenchmarkRates';
import { useBenchmarkRatePeriods } from '../../hooks/useBenchmarkRatePeriods';
import { useBaseAssetTypes } from '../../hooks/useBaseAssetTypes';
import { useCreditRiskTypes } from '../../hooks/useCreditRiskTypes';
import type { Asset } from '../../types/Asset';
import { isAssetFieldApplicable } from '../../config/assetTypeFields';
import { dateColumnProps } from '../../utils/dateColumn';
import { t } from '../../i18n/t';

const newAssetDefaults = (): Partial<Asset> => ({
  assetId: 0,
  assetName: null,
  assetTypeId: 0,
  symbol: null,
  crcyId: null,
  benchmarkRateId: null,
  benchmarkRatePeriodId: null,
  expDate: null,
  idxId: null,
  multiplier: null,
  value1Point: null,
  isForeign: null,
  linkageCrcyId: null,
  baseAssetTypeId: null,
  creditRiskTypeId: null,
  ticker: null,
  shortName: null,
  isin: null,
  isin4Isa: null,
  bloombergTradePlaceId: null,
  isPreMarketExchange: null,
  isDynamicInterest: null,
  countryCode: null,
  industryGroupId: null,
  industrySectorId: null,
  isGovernment: null,
  strike: null,
  callOrPut: null,
  underlyingAssetId: null,
});

export function AssetsPage() {
  // Lazy load: both lookup lists are fetched only when this page mounts.
  const {
    data: assetTypes,
    loading: assetTypesLoading,
    error: assetTypesError,
  } = useAssetTypes();
  const {
    data: crcies,
    loading: crciesLoading,
    error: crciesError,
  } = useCrcies();
  const {
    data: benchmarkRates,
    loading: benchmarkRatesLoading,
    error: benchmarkRatesError,
  } = useBenchmarkRates();
  const {
    data: benchmarkRatePeriods,
    loading: benchmarkRatePeriodsLoading,
    error: benchmarkRatePeriodsError,
  } = useBenchmarkRatePeriods();
  const {
    data: baseAssetTypes,
    loading: baseAssetTypesLoading,
    error: baseAssetTypesError,
  } = useBaseAssetTypes();
  const {
    data: creditRiskTypes,
    loading: creditRiskTypesLoading,
    error: creditRiskTypesError,
  } = useCreditRiskTypes();

  const columnDefs = useMemo<ColDef<Asset>[]>(() => {
    if (
      !assetTypes ||
      !crcies ||
      !benchmarkRates ||
      !benchmarkRatePeriods ||
      !baseAssetTypes ||
      !creditRiskTypes
    )
      return [];

    const noneLabel = t('assets.noneOption');

    // AssetType names are NOT unique (e.g. "מבוטל" appears twice), so the
    // dropdown is keyed by a unique "<code> - <name>" label.
    const assetTypeValues = assetTypes.map(
      (a) => `${a.assetTypeId} - ${a.assetTypeName}`,
    );
    const assetTypeLabelById = new Map<number, string>(
      assetTypes.map((a) => [
        a.assetTypeId,
        `${a.assetTypeId} - ${a.assetTypeName}`,
      ]),
    );
    const assetTypeIdByLabel = new Map<string, number>(
      assetTypes.map((a) => [
        `${a.assetTypeId} - ${a.assetTypeName}`,
        a.assetTypeId,
      ]),
    );

    // Currency names: nullable FK, so the dropdown includes a "none" option.
    const crcyValues = [
      noneLabel,
      ...crcies.map((c) => `${c.crcyId} - ${c.crcyName}`),
    ];
    const crcyLabelById = new Map<number, string>(
      crcies.map((c) => [c.crcyId, `${c.crcyId} - ${c.crcyName}`]),
    );
    const crcyIdByLabel = new Map<string, number>(
      crcies.map((c) => [`${c.crcyId} - ${c.crcyName}`, c.crcyId]),
    );

    // Benchmark rates: nullable FK, so the dropdown includes a "none" option.
    const benchmarkRateValues = [
      noneLabel,
      ...benchmarkRates.map(
        (b) => `${b.benchmarkRateId} - ${b.benchmarkRateName}`,
      ),
    ];
    const benchmarkRateLabelById = new Map<number, string>(
      benchmarkRates.map((b) => [
        b.benchmarkRateId,
        `${b.benchmarkRateId} - ${b.benchmarkRateName}`,
      ]),
    );
    const benchmarkRateIdByLabel = new Map<string, number>(
      benchmarkRates.map((b) => [
        `${b.benchmarkRateId} - ${b.benchmarkRateName}`,
        b.benchmarkRateId,
      ]),
    );

    // Benchmark rate periods: nullable FK, so the dropdown includes a "none" option.
    const benchmarkRatePeriodValues = [
      noneLabel,
      ...benchmarkRatePeriods.map(
        (b) => `${b.benchmarkRatePeriodId} - ${b.benchmarkRatePeriodName}`,
      ),
    ];
    const benchmarkRatePeriodLabelById = new Map<number, string>(
      benchmarkRatePeriods.map((b) => [
        b.benchmarkRatePeriodId,
        `${b.benchmarkRatePeriodId} - ${b.benchmarkRatePeriodName}`,
      ]),
    );
    const benchmarkRatePeriodIdByLabel = new Map<string, number>(
      benchmarkRatePeriods.map((b) => [
        `${b.benchmarkRatePeriodId} - ${b.benchmarkRatePeriodName}`,
        b.benchmarkRatePeriodId,
      ]),
    );

    // Linkage currency (hatzmadaCrcyId): nullable FK, reuses the CRCY lookup.
    const linkageCrcyValues = [
      noneLabel,
      ...crcies.map((c) => `${c.crcyId} - ${c.crcyName}`),
    ];

    // Base asset type (commodity / bond / stock): nullable FK -> BaseAssetTypes.
    const baseAssetTypeValues = [
      noneLabel,
      ...baseAssetTypes.map((b) => `${b.baseAssetTypeId} - ${b.baseAssetTypeName}`),
    ];
    const baseAssetTypeLabelById = new Map<number, string>(
      baseAssetTypes.map((b) => [
        b.baseAssetTypeId,
        `${b.baseAssetTypeId} - ${b.baseAssetTypeName}`,
      ]),
    );
    const baseAssetTypeIdByLabel = new Map<string, number>(
      baseAssetTypes.map((b) => [
        `${b.baseAssetTypeId} - ${b.baseAssetTypeName}`,
        b.baseAssetTypeId,
      ]),
    );

    // Credit risk type: nullable FK -> CreditRiskTypes.
    const creditRiskTypeValues = [
      noneLabel,
      ...creditRiskTypes.map((c) => `${c.creditRiskTypeId} - ${c.creditRiskTypeName}`),
    ];
    const creditRiskTypeLabelById = new Map<number, string>(
      creditRiskTypes.map((c) => [
        c.creditRiskTypeId,
        `${c.creditRiskTypeId} - ${c.creditRiskTypeName}`,
      ]),
    );
    const creditRiskTypeIdByLabel = new Map<string, number>(
      creditRiskTypes.map((c) => [
        `${c.creditRiskTypeId} - ${c.creditRiskTypeName}`,
        c.creditRiskTypeId,
      ]),
    );

    return [
      {
        field: 'assetId',
        headerName: t('assets.columns.assetId'),
        width: 120,
        // Natural PK: editable on Add only; existing rows must keep their code.
        editable: (params: EditableCallbackParams<Asset>) =>
          (params.data as { __status?: string } | undefined)?.__status ===
          'added',
        cellDataType: 'number',
        sort: 'asc',
      },
      {
        field: 'assetName',
        headerName: t('assets.columns.assetName'),
        flex: 1,
        editable: true,
      },
      {
        field: 'assetTypeId',
        headerName: t('assets.columns.assetTypeId'),
        width: 220,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: assetTypeValues },
        valueGetter: (p: ValueGetterParams<Asset>) => {
          if (!p.data) return '';
          // assetTypeId is REQUIRED (NOT NULL) — no none option. Fall back to
          // the raw id as a string for dirty data not present in the lookup.
          return (
            assetTypeLabelById.get(p.data.assetTypeId) ??
            String(p.data.assetTypeId)
          );
        },
        valueSetter: (p: ValueSetterParams<Asset>) => {
          if (!p.data) return false;
          const newId = assetTypeIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.assetTypeId = newId;
          return true;
        },
      },
      {
        field: 'symbol',
        headerName: t('assets.columns.symbol'),
        flex: 1,
        editable: true,
      },
      {
        // V1.27.0 — per-security multiplier (כופל). FLOAT NULL.
        field: 'multiplier',
        headerName: t('assets.columns.multiplier'),
        width: 110,
        editable: true,
        cellDataType: 'number',
      },
      {
        // V1.73.30 — value of one point of a derivative (ערך נקודה). FLOAT NULL.
        field: 'value1Point',
        headerName: t('assets.columns.value1Point'),
        width: 110,
        editable: true,
        cellDataType: 'number',
      },
      {
        field: 'crcyId',
        headerName: t('assets.columns.crcyId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: crcyValues },
        valueGetter: (p: ValueGetterParams<Asset>) => {
          if (!p.data) return noneLabel;
          if (p.data.crcyId == null) return noneLabel;
          return crcyLabelById.get(p.data.crcyId) ?? noneLabel;
        },
        valueSetter: (p: ValueSetterParams<Asset>) => {
          if (!p.data) return false;
          if (p.newValue === noneLabel) {
            p.data.crcyId = null;
            return true;
          }
          const newId = crcyIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.crcyId = newId;
          return true;
        },
      },
      {
        field: 'benchmarkRateId',
        headerName: t('assets.columns.benchmarkRateId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: benchmarkRateValues },
        valueGetter: (p: ValueGetterParams<Asset>) => {
          if (!p.data) return noneLabel;
          if (p.data.benchmarkRateId == null) return noneLabel;
          return benchmarkRateLabelById.get(p.data.benchmarkRateId) ?? noneLabel;
        },
        valueSetter: (p: ValueSetterParams<Asset>) => {
          if (!p.data) return false;
          if (p.newValue === noneLabel) {
            p.data.benchmarkRateId = null;
            return true;
          }
          const newId = benchmarkRateIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.benchmarkRateId = newId;
          return true;
        },
      },
      {
        field: 'benchmarkRatePeriodId',
        headerName: t('assets.columns.benchmarkRatePeriodId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: benchmarkRatePeriodValues },
        valueGetter: (p: ValueGetterParams<Asset>) => {
          if (!p.data) return noneLabel;
          if (p.data.benchmarkRatePeriodId == null) return noneLabel;
          return (
            benchmarkRatePeriodLabelById.get(p.data.benchmarkRatePeriodId) ??
            noneLabel
          );
        },
        valueSetter: (p: ValueSetterParams<Asset>) => {
          if (!p.data) return false;
          if (p.newValue === noneLabel) {
            p.data.benchmarkRatePeriodId = null;
            return true;
          }
          const newId = benchmarkRatePeriodIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.benchmarkRatePeriodId = newId;
          return true;
        },
      },
      {
        field: 'expDate',
        headerName: t('assets.columns.expDate'),
        width: 140,
        editable: true,
        ...dateColumnProps<Asset>('expDate'),
      },
      {
        field: 'idxId',
        headerName: t('assets.columns.idxId'),
        width: 120,
        editable: true,
        cellDataType: 'number',
      },
      // ---- V1.71.3 options / Black-Scholes columns ----
      // A grid column can't be hidden per-row, so instead we gate editability by
      // the asset-type applicability map (all types allowed today; tighten there).
      {
        field: 'strike',
        headerName: t('assets.columns.strike'),
        width: 110,
        editable: (p: EditableCallbackParams<Asset>) => isAssetFieldApplicable('strike', p.data?.assetTypeId),
        cellDataType: 'number',
      },
      {
        field: 'callOrPut',
        headerName: t('assets.columns.callOrPut'),
        width: 100,
        editable: (p: EditableCallbackParams<Asset>) => isAssetFieldApplicable('callOrPut', p.data?.assetTypeId),
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: ['', 'C', 'P'] },
      },
      {
        field: 'underlyingAssetId',
        headerName: t('assets.columns.underlyingAssetId'),
        width: 150,
        editable: (p: EditableCallbackParams<Asset>) => isAssetFieldApplicable('underlyingAssetId', p.data?.assetTypeId),
        cellDataType: 'number',
      },
      // ---- V1.56.0 valuation-engine columns ----
      {
        field: 'isForeign',
        headerName: t('assets.columns.isForeign'),
        width: 110,
        editable: true,
        cellDataType: 'boolean',
      },
      {
        field: 'linkageCrcyId',
        headerName: t('assets.columns.linkageCrcyId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: linkageCrcyValues },
        valueGetter: (p: ValueGetterParams<Asset>) => {
          if (!p.data || p.data.linkageCrcyId == null) return noneLabel;
          return crcyLabelById.get(p.data.linkageCrcyId) ?? noneLabel;
        },
        valueSetter: (p: ValueSetterParams<Asset>) => {
          if (!p.data) return false;
          if (p.newValue === noneLabel) {
            p.data.linkageCrcyId = null;
            return true;
          }
          const newId = crcyIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.linkageCrcyId = newId;
          return true;
        },
      },
      {
        field: 'baseAssetTypeId',
        headerName: t('assets.columns.baseAssetTypeId'),
        width: 180,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: baseAssetTypeValues },
        valueGetter: (p: ValueGetterParams<Asset>) => {
          if (!p.data || p.data.baseAssetTypeId == null) return noneLabel;
          return (
            baseAssetTypeLabelById.get(p.data.baseAssetTypeId) ??
            String(p.data.baseAssetTypeId)
          );
        },
        valueSetter: (p: ValueSetterParams<Asset>) => {
          if (!p.data) return false;
          if (p.newValue === noneLabel) {
            p.data.baseAssetTypeId = null;
            return true;
          }
          const newId = baseAssetTypeIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.baseAssetTypeId = newId;
          return true;
        },
      },
      {
        field: 'creditRiskTypeId',
        headerName: t('assets.columns.creditRiskTypeId'),
        width: 220,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: creditRiskTypeValues },
        valueGetter: (p: ValueGetterParams<Asset>) => {
          if (!p.data || p.data.creditRiskTypeId == null) return noneLabel;
          return (
            creditRiskTypeLabelById.get(p.data.creditRiskTypeId) ??
            String(p.data.creditRiskTypeId)
          );
        },
        valueSetter: (p: ValueSetterParams<Asset>) => {
          if (!p.data) return false;
          if (p.newValue === noneLabel) {
            p.data.creditRiskTypeId = null;
            return true;
          }
          const newId = creditRiskTypeIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.creditRiskTypeId = newId;
          return true;
        },
      },
      {
        field: 'ticker',
        headerName: t('assets.columns.ticker'),
        width: 120,
        editable: true,
      },
      {
        field: 'shortName',
        headerName: t('assets.columns.shortName'),
        flex: 1,
        editable: true,
      },
      {
        field: 'isin',
        headerName: t('assets.columns.isin'),
        width: 150,
        editable: true,
      },
      {
        field: 'isin4Isa',
        headerName: t('assets.columns.isin4Isa'),
        width: 150,
        editable: true,
      },
      {
        field: 'bloombergTradePlaceId',
        headerName: t('assets.columns.bloombergTradePlaceId'),
        width: 160,
        editable: true,
      },
      {
        field: 'countryCode',
        headerName: t('assets.columns.countryCode'),
        width: 120,
        editable: true,
      },
      {
        field: 'industryGroupId',
        headerName: t('assets.columns.industryGroupId'),
        width: 130,
        editable: true,
        cellDataType: 'number',
      },
      {
        field: 'industrySectorId',
        headerName: t('assets.columns.industrySectorId'),
        width: 130,
        editable: true,
        cellDataType: 'number',
      },
      {
        field: 'isGovernment',
        headerName: t('assets.columns.isGovernment'),
        width: 110,
        editable: true,
        cellDataType: 'boolean',
      },
      {
        field: 'isPreMarketExchange',
        headerName: t('assets.columns.isPreMarketExchange'),
        width: 130,
        editable: true,
        cellDataType: 'boolean',
      },
      {
        field: 'isDynamicInterest',
        headerName: t('assets.columns.isDynamicInterest'),
        width: 130,
        editable: true,
        cellDataType: 'boolean',
      },
    ];
  }, [
    assetTypes,
    crcies,
    benchmarkRates,
    benchmarkRatePeriods,
    baseAssetTypes,
    creditRiskTypes,
  ]);

  const error =
    assetTypesError ??
    crciesError ??
    benchmarkRatesError ??
    benchmarkRatePeriodsError ??
    baseAssetTypesError ??
    creditRiskTypesError;
  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('assets.errorPrefix', { message: error })}
        </div>
      </div>
    );
  }

  // Render the grid once BOTH lookups are loaded.
  if (
    assetTypesLoading ||
    crciesLoading ||
    benchmarkRatesLoading ||
    benchmarkRatePeriodsLoading ||
    baseAssetTypesLoading ||
    creditRiskTypesLoading ||
    !assetTypes ||
    !crcies ||
    !benchmarkRates ||
    !benchmarkRatePeriods ||
    !baseAssetTypes ||
    !creditRiskTypes
  ) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <LopCrudPage<Asset>
      resourceCode="assets"
      titleKey="assets.pageTitle"
      idField="assetId"
      getRows={getAssets}
      saveBatch={(added, modified, deletedIds) =>
        saveAssetsBatch(added, modified, deletedIds as number[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newAssetDefaults}
    />
  );
}
